import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DEBOUNCE_TIME } from '../../utils/constants';
import { IoMdClose } from 'react-icons/io';

interface EmployeeUploadSearchProps {
  value: string;
  placeholder?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSearch: () => void;
  onClear: () => void;
}

const SearchSvgIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M14 14L11.1 11.1M12.6667 7.33333C12.6667 10.2789 10.2789 12.6667 7.33333 12.6667C4.38781 12.6667 2 10.2789 2 7.33333C2 4.38781 4.38781 2 7.33333 2C10.2789 2 12.6667 4.38781 12.6667 7.33333Z"
      stroke="#2C2C2C"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EmployeeUploadSearch: React.FC<EmployeeUploadSearchProps> = ({
  value,
  placeholder = "Search",
  onChange,
  onSearch,
  onClear,
}) => {
  const [focused, setFocused] = useState(false);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const wasFocusedRef = useRef(false);
  const caretRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (
      wasFocusedRef.current &&
      inputRef.current &&
      document.activeElement !== inputRef.current
    ) {
      const el = inputRef.current;
      el.focus();
      const pos = caretRef.current != null ? caretRef.current : el.value.length;
      try {
        el.setSelectionRange(pos, pos);
      } catch (_err) {
        /* ignore */
      }
    }
  });

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    caretRef.current = e.target.selectionStart;
    onChange(e);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      onSearchRef.current();
    }, DEBOUNCE_TIME);
  };

  const borderColor = focused ? "var(--royal-blue, #5048E5)" : "#D9D9D9";

  return (
    <div
      style={{
        position: "relative",
        width: "240px",
        height: "36px",
        borderRadius: "9999px",
        border: `1px solid ${borderColor}`,
        backgroundColor: "#ffffff",
        flexShrink: 0,
      }}
    >
      <input
        ref={inputRef}
        type="text"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          padding: value ? "8px 60px 8px 14px" : "8px 36px 8px 14px",
          border: "none",
          outline: "none",
          borderRadius: "9999px",
          fontSize: "14px",
          fontWeight: 500,
          backgroundColor: "transparent",
          color: "#2C2C2C",
        }}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={(e) => { if (e.key === 'Enter') onSearch(); }}
        onFocus={() => { setFocused(true); wasFocusedRef.current = true; }}
        onBlur={(e) => {
          setFocused(false);
          // Only mark as not-focused if the blur is from a real user action,
          // not a transient blur caused by React re-render replacing the node.
          const next = e.relatedTarget as Node | null;
          if (next !== inputRef.current) {
            wasFocusedRef.current = false;
          }
        }}
      />

      {value && (
        <button
          style={{
            position: "absolute",
            right: "36px",
            top: "50%",
            transform: "translateY(-50%)",
            background: "none",
            border: "none",
            padding: "0 4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            color: "#707070",
          }}
          onClick={onClear}
        >
          <IoMdClose size={14} />
        </button>
      )}

      <button
        style={{
          position: "absolute",
          right: 0,
          top: 0,
          width: "36px",
          height: "36px",
          background: "none",
          border: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          padding: 0,
        }}
        onClick={onSearch}
      >
        <SearchSvgIcon />
      </button>
    </div>
  );
};

export default EmployeeUploadSearch;
